import type { KeyboardEvent, ReactNode } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { FoodsTags, ToggleSwitch, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { Score } from "src/types";
import { hasNoEligibleEntityType } from "src/utils/scores";
import { ScoreSettingsSummary } from "../utils/scoreTypeSettings";

type ScoreRowProps = {
    score: Score;
    summary: ScoreSettingsSummary;
    position?: number;
    dragHandle?: ReactNode;
    isEnabled: boolean;
    isSelected: boolean;
    canModify: boolean;
    onSelect: () => void;
    onToggle: () => void;
};

export const ScoreRow = (props: ScoreRowProps) => {
    const { score, summary, position, dragHandle, isEnabled, isSelected, canModify, onSelect, onToggle } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    const summaryParts = [
        summary.variationCount > 0 &&
            t("settings.scores.summary.variations", "Variations: {{enabled}} of {{total}}", {
                enabled: summary.enabledVariationCount,
                total: summary.variationCount,
            }),
        summary.subscoreCount > 0 &&
            t("settings.scores.summary.subscores", "Sub-scores: {{enabled}} of {{total}}", {
                enabled: summary.enabledSubscoreCount,
                total: summary.subscoreCount,
            }),
    ].filter(Boolean);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect();
        }
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={theme.spacing(1.5)}
            onClick={onSelect}
            sx={{
                padding: theme.spacing(1.5, 2, 1.5, 1),
                borderBottom: `1px solid ${theme.custom.grey[500]}`,
                borderRadius: theme.spacing(1),
                backgroundColor: isSelected ? theme.custom.grey[200] : "transparent",
                cursor: "pointer",
                "&:hover": { backgroundColor: theme.custom.grey[200] },
            }}
        >
            {dragHandle && <Box onClick={(event) => event.stopPropagation()}>{dragHandle}</Box>}
            {position !== undefined && (
                <Typography variant="small-bold" color={theme.custom.grey[1500]}>
                    {position}
                </Typography>
            )}
            <Stack
                flex={1}
                minWidth={0}
                gap={theme.spacing(0.5)}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onKeyDown={handleKeyDown}
                sx={{ "&:focus-visible": { outline: `2px solid ${theme.palette.primary[600]}` } }}
            >
                <Typography variant="body-semibold">{score.title}</Typography>
                {summaryParts.length > 0 && (
                    <Typography variant="small-regular" color={theme.custom.grey[1500]}>
                        {summaryParts.join(" · ")}
                    </Typography>
                )}
            </Stack>
            {score.isChildOfEveryMainScore && (
                <FoodsTags
                    variant="tags-grey"
                    label={t("settings.scores.score_child_of_every_main_score", "Child of every main score")}
                />
            )}
            {hasNoEligibleEntityType(score) && (
                <FoodsTags variant="tags-grey" label={t("settings.scores.no_eligible_entity", "No entity in SURE")} />
            )}
            <Box onClick={(event) => event.stopPropagation()}>
                <ToggleSwitch
                    toggleSize="small"
                    checked={isEnabled}
                    disabled={!canModify}
                    onChange={onToggle}
                    inputProps={{ "aria-label": score.title }}
                />
            </Box>
        </Stack>
    );
};
